import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { db } from "../firebase";
import {
  collection,
  orderBy,
  query,
  onSnapshot,
} from "firebase/firestore";

export default function History() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 640);
  const userId = localStorage.getItem("currentUser");

  // Responsive handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Safe currency formatter
  const formatCurrency = (num) => {
    const value = Number(num) || 0;
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });
  };

  useEffect(() => {
    if (!userId) return;

    const withdrawalsQuery = query(
      collection(db, "users", userId, "withdrawals"),
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(withdrawalsQuery, (querySnapshot) => {
      const txns = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();

        txns.push({
          id: doc.id,
          amount: Number(data.amount) || 0,
          balanceAfter: Number(data.balanceAfter) || 0,
          accountNumber: data.accountNumber,
          timestamp: data.timestamp?.toDate() || new Date(),
          status: data.status || "Pending",
          type: data.type || "Withdrawal",
          description:
            data.description?.trim() ||
            (data.type === "Deposit"
              ? "Deposit"
              : data.type === "Daily Interest"
              ? "Daily Interest Earning"
              : `Withdrawal to Account #${data.accountNumber?.slice(-4) || "****"}`),
        });
      });

      setTransactions(txns);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  const getStatusBadge = (status) => {
    if (status === "Completed") {
      return (
        <span
          style={{
            backgroundColor: "#1e40af",
            color: "white",
            padding: "0.25rem 0.6rem",
            borderRadius: "9999px",
            fontSize: "0.65rem",
            fontWeight: 600,
          }}
        >
          Completed
        </span>
      );
    }
    return (
      <span
        style={{
          backgroundColor: "#e5e7eb",
          color: "#111827",
          padding: "0.25rem 0.6rem",
          borderRadius: "9999px",
          fontSize: "0.65rem",
          fontWeight: 500,
        }}
      >
        Pending
      </span>
    );
  };

  return (
    <div className="dashboard-container">
      <Navbar />
      <main
        className="dashboard-main"
        style={{
          maxWidth: isMobile ? "100%" : "960px",
          margin: "0 auto",
          padding: isMobile ? "1rem 0.5rem" : "2rem 1rem",
          fontFamily: "sans-serif",
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            padding: "1rem",
          }}
        >
          <h2 style={{ fontSize: "1.2rem", marginBottom: "0.3rem" }}>
            Transaction History
          </h2>
          <p style={{ color: "#6b7280", marginBottom: "1rem", fontSize: "0.8rem" }}>
            Review your recent account activity.
          </p>

          {loading ? (
            <p>Loading transactions...</p>
          ) : transactions.length === 0 ? (
            <p>No transactions found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", backgroundColor: "#f9fafb" }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Description</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Balance After</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((txn) => {
                    const isWithdrawal = txn.type === "Withdrawal";
                    const amountClass = isWithdrawal
                      ? "amount-negative"
                      : txn.amount >= 0
                      ? "amount-positive"
                      : "amount-negative";

                    return (
                      <tr key={txn.id} style={{ borderTop: "1px solid #f1f1f1" }}>
                        <td style={tdStyle}>
                          {txn.timestamp.toISOString().slice(0, 16).replace("T", " ")}
                        </td>
                        <td style={tdStyle}>
                          <span style={{ fontSize: "0.7rem", color: "#374151" }}>
                            {txn.type}
                          </span>
                        </td>
                        <td style={tdStyle}>{txn.description}</td>
                        <td style={tdStyle}>{getStatusBadge(txn.status)}</td>
                        <td style={tdStyle}>
                          <span className={amountClass} style={{ fontWeight: 600 }}>
                            {isWithdrawal
                              ? `-${formatCurrency(Math.abs(txn.amount))}`
                              : `+${formatCurrency(txn.amount)}`}
                          </span>
                        </td>
                        <td style={tdStyle}>{formatCurrency(txn.balanceAfter ?? 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

const thStyle = {
  padding: "0.4rem",
  fontSize: "0.7rem",
  color: "#374151",
  fontWeight: 600,
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "0.4rem",
  fontSize: "0.7rem",
  color: "#374151",
  verticalAlign: "top",
  whiteSpace: "nowrap",
  maxWidth: "140px",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
